import React from "react";
import {
  FiSearch,
  FiX,
  FiPlus,
  FiFilter,
  FiLayers,
  FiClock,
} from "react-icons/fi";

const ListHeaderToolbar = ({
  title,
  subtitle,
  icon: Icon,
  totalCount = 0,
  activeCount = 0,
  finishedCount = 0,
  pendingCount = 0,
  // Search state
  searchText,
  setSearchText,
  searchPlaceholder = "Որոնել ըստ վերնագրի կամ հեղինակի...",
  // Status filter
  statusFilter,
  setStatusFilter,
  statusOptions = [
    { id: "all", label: "Բոլորը" },
    { id: "active", label: "Ակտիվ" },
    { id: "finished", label: "Ավարտված" },
    { id: "pending", label: "Սևագիր" },
  ],
  // Outcome filter
  outcomeFilter,
  setOutcomeFilter,
  outcomeOptions,
  // Category filter
  categoryFilter,
  setCategoryFilter,
  categoryOptions = [],
  // Sort
  sortBy,
  setSortBy,
  // Create button
  onCreateNew,
  createButtonText,
  canCreate = false,
  // Reset
  onResetFilters,
  hasActiveFilters,
  filteredCount,
}) => {
  return (
    <div className="space-y-5 mb-6">
      {/* 1. Page Header & Stats */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          {Icon && (
            <span className="p-3 rounded-2xl bg-indigo-50 text-indigo-600 border border-indigo-100 shadow-xs flex items-center justify-center">
              <Icon size={24} />
            </span>
          )}
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                {title}
              </h1>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                {totalCount}
              </span>
            </div>
            {subtitle ? (
              <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
            ) : (
              <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                <span>
                  Ակտիվ: <b className="text-slate-800">{activeCount}</b>
                </span>
                <span className="text-slate-300">•</span>
                <span>
                  Ավարտված: <b className="text-slate-800">{finishedCount}</b>
                </span>
                {pendingCount > 0 && (
                  <>
                    <span className="text-slate-300">•</span>
                    <span>
                      Սևագիր: <b className="text-slate-800">{pendingCount}</b>
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Create Button */}
        {canCreate && onCreateNew && (
          <button
            onClick={onCreateNew}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-semibold text-sm shadow-xs transition-all shrink-0 hover:shadow-md cursor-pointer"
          >
            <FiPlus size={18} strokeWidth={2.5} />
            <span>{createButtonText || "Ստեղծել նորը"}</span>
          </button>
        )}
      </div>

      {/* 2. Search & Filter Bar */}
      <div className="bg-white border border-slate-200/80 rounded-2xl p-3.5 sm:p-4 shadow-xs space-y-3.5">
        {/* Search Input & Segmented Status Controls */}
        <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center justify-between">
          {/* Search Input */}
          <div className="relative flex-1 max-w-xl">
            <FiSearch
              size={17}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full bg-slate-50 hover:bg-slate-100/60 focus:bg-white border border-slate-200 focus:border-indigo-500 rounded-xl pl-10 pr-9 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/15 transition"
            />
            {searchText && (
              <button
                type="button"
                onClick={() => setSearchText("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200/60 transition"
              >
                <FiX size={15} />
              </button>
            )}
          </div>

          {/* Status Segmented Buttons */}
          {statusOptions && (
            <div className="flex items-center bg-slate-100 p-1 rounded-xl shrink-0 overflow-x-auto">
              {statusOptions.map((opt) => {
                const isSelected = statusFilter === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setStatusFilter(opt.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition whitespace-nowrap cursor-pointer ${
                      isSelected
                        ? "bg-white text-slate-900 shadow-xs"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Secondary Filter Row: Category, Outcome, Sort & Reset */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100 text-xs">
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Category Filter */}
            {categoryOptions && categoryOptions.length > 0 && (
              <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                <FiLayers className="text-slate-400" size={13} />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="bg-transparent text-xs font-medium text-slate-700 focus:outline-none cursor-pointer"
                >
                  <option value="all">Բոլոր կատեգորիաները</option>
                  {categoryOptions.map((cat) => (
                    <option key={cat.value || cat} value={cat.value || cat}>
                      {cat.label || cat}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Outcome Filter (Optional) */}
            {outcomeOptions && (
              <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                <FiFilter className="text-slate-400" size={13} />
                <select
                  value={outcomeFilter}
                  onChange={(e) => setOutcomeFilter(e.target.value)}
                  className="bg-transparent text-xs font-medium text-slate-700 focus:outline-none cursor-pointer"
                >
                  {outcomeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Sort Order */}
            {sortBy && setSortBy && (
              <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                <FiClock className="text-slate-400" size={13} />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-transparent text-xs font-medium text-slate-700 focus:outline-none cursor-pointer"
                >
                  <option value="newest">Նորագույնները</option>
                  <option value="oldest">Հնագույնները</option>
                  <option value="deadline">Մոտալուտ վերջնաժամկետ</option>
                </select>
              </div>
            )}
          </div>

          {/* Result count & Clear filter button */}
          <div className="flex items-center gap-3 ml-auto">
            {hasActiveFilters && (
              <>
                <button
                  type="button"
                  onClick={onResetFilters}
                  className="text-indigo-600 hover:text-indigo-800 font-medium hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <FiX size={13} />
                  Մաքրել ֆիլտրերը
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListHeaderToolbar;
